<script setup lang="ts">
import {
  HgSupportedLanguage,
  Is24Layout,
  Is24MainNavItem,
  Is24SideMenuNavItem,
  MenuItem,
} from '@smg-real-estate/domus';
import { useExperimentsStore } from '@smg-real-estate/openfeature-vue';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onMounted, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useAuth0Client } from '@/composables/useAuth0Client';
import { useExperimentConsentRefresh } from '@/composables/useExperimentConsentRefresh';
import { useRouting } from '@/composables/useRouting';
import { checkUserAuthenticated } from '@/helpers/Auth/authClient';
import { getAppMetaInfo } from '@/helpers/Meta/metaInfoHelper';
import {
  getPage,
  isFunnelPage,
  isIV24FunnelPage,
  isIV24ResultPage,
} from '@/helpers/Route/routeHelper';
import { useCommonStore } from '@/stores/common/common';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { useAppTracking } from '@/tracking/tracking';
import { Pages, PlatformName } from '@/types';
import initIframeResizer from './iframeResizer';
import { SplitFeatureFlags } from './splitExperiments/splitFeatureFlags';

// Composables
const propertyValuationStore = usePropertyValuationStore();
const { isIv24FunnelActive } = storeToRefs(propertyValuationStore);
const commonStore = useCommonStore();
const experimentsStore = useExperimentsStore();
const route = useRoute();
useExperimentConsentRefresh();
const { tm, locale } = useI18n();
const { getLanguageSwitchLink } = useRouting();
const auth0 = useAuth0Client();
// Reactive data
const baseUrl = ref(import.meta.env.VUE_APP_BASE_URL);
const isLoggedIn = ref(false);
const authToken = ref<string | undefined>();
const {
  handleMainNavTracking,
  handleMobileMenuNavTracking,
  handleTrackFooterClick,
  handleTrackHeaderClick,
} = useAppTracking(locale);

// Head management
useHead(() => getAppMetaInfo(locale.value as HgSupportedLanguage));

// Computed properties
const isNewNavigationEnabled = computed(() =>
  experimentsStore.isTreatmentOn(SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);

const userAccountMenuItem = computed(
  (): MenuItem => ({
    label: tm('$is24.nav.harmonize.overviewMenuItem.label'),
    href: `${baseUrl.value}/${locale.value}/account-overview`,
  }),
);

const favouritesMenuItem = computed(
  (): MenuItem => tm('$is24.nav.favoriteMenuItem') as unknown as MenuItem,
);

const dropdownMenuItems = computed((): MenuItem => {
  if (isLoggedIn.value) {
    return {
      label: tm('$is24.nav.loggedInMenuItem.label'),
      children: tm('$is24.nav.harmonize.privateItems') as unknown as MenuItem[] as any,
    };
  } else {
    return {
      label: tm('$is24.nav.harmonize.overviewMenuItem.label'),
      href: `${baseUrl.value}/${locale.value}/account-overview`,
    };
  }
});

const adMenuItem = computed(
  (): MenuItem =>
    tm(
      isNewNavigationEnabled.value
        ? '$is24.nav.topNavNewListingMenuItem'
        : '$is24.nav.harmonize.newListingMenuItem',
    ) as unknown as MenuItem,
);

const isFooterHidden = computed((): boolean => isFunnelPage(route.path));
const isProd = computed((): boolean => import.meta.env.VUE_APP_STAGE === 'prod');

// Methods
const setCurrentPage = (page: Pages) => commonStore.setCurrentPage(page);
const setLoggedInUser = (user: string) => commonStore.setLoggedInUser(user);
const resetUser = () => commonStore.resetUser();
const setIframeActive = (active: boolean) => propertyValuationStore.setIframeActive(active);

const updateUserDetails = async () => {
  if (await checkUserAuthenticated()) {
    isLoggedIn.value = true;
    setLoggedInUser(auth0?.user.value?.['cognito:username'] || '');
    authToken.value = await auth0?.getAccessTokenSilently();
  } else {
    isLoggedIn.value = false;
    resetUser();
  }
};

const handleFavouritesClick = () => {
  handleTrackHeaderClick({
    eventAction: 'open_favourites',
    eventLabel: 'tab_navigation',
    contentLanguage: locale.value,
  });
};

// Watchers
watch(
  () => route.path,
  (path: string) => {
    const currentPage = getPage(path);
    if (!currentPage) {
      return;
    }

    setCurrentPage(currentPage);

    if (isIV24FunnelPage(route.path) || isIV24ResultPage(route.path)) {
      setIframeActive(true);
    }
  },
);

onBeforeMount(() => {
  initIframeResizer();
});

// Lifecycle hooks
onMounted(async () => {
  await updateUserDetails();
});

onServerPrefetch(() => {
  const currentPage = getPage(route.path);
  if (!currentPage) {
    return;
  }

  if (isIV24FunnelPage(route.path) || isIV24ResultPage(route.path)) {
    setIframeActive(true);
  }

  setCurrentPage(currentPage);
});
</script>

<template>
  <Is24Layout
    id="app"
    :baseUrl="baseUrl"
    :currentLanguage="locale"
    :currentRoute="route"
    :getLanguageToLink="getLanguageSwitchLink"
    :hideFooter="isFooterHidden"
    :hideMainNavigation="false"
    :isWhiteLabelSolution="isIv24FunnelActive"
    :isTopNavEnabled="isNewNavigationEnabled"
    :isUserLoggedIn="isLoggedIn"
    @trackFooterClick="handleTrackFooterClick"
    @trackHeaderClick="handleTrackHeaderClick"
  >
    <template #headerMetaAction>
      <Is24MainNavItem
        :menuItem="adMenuItem"
        :currentRoute="route"
        :baseUrl="baseUrl"
        :bordered="true"
        @click="
          handleTrackHeaderClick({
            eventAction: 'open_insertAd',
            eventLabel: 'tab_navigation',
            contentLanguage: $i18n.locale,
          })
        "
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :menuNavItem="Is24MainNavItem"
        :platform="PlatformName.Immoscout24"
        :isProduction="isProd"
        :authToken="authToken"
        :route="route"
        :baseUrl="baseUrl"
        @click="handleFavouritesClick"
      />
      <Is24MainNavItem
        :menuItem="dropdownMenuItems"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="route"
        @mainNavItemClick="handleMainNavTracking"
        @dropDownMenuItemClick="handleMainNavTracking"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :menuNavItem="Is24SideMenuNavItem"
        :platform="PlatformName.Immoscout24"
        :isProduction="isProd"
        :authToken="authToken"
        :route="route"
        :baseUrl="baseUrl"
        @click="handleFavouritesClick"
      />
      <template v-if="!isLoggedIn">
        <Is24SideMenuNavItem
          :menuItem="userAccountMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          :currentRoute="route"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
      </template>
    </template>
    <template #sideMenuAccountNavigation>
      <template v-if="isLoggedIn">
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.overviewMenuItem') as unknown as MenuItem"
          icon="PersonOutline"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.settingsMenuItem') as unknown as MenuItem"
          icon="Settings"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myAdsMenuItem') as unknown as MenuItem"
          icon="Appointment"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myRealEstateMenuItem') as unknown as MenuItem"
          icon="House"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.searchAlertMenuItem') as unknown as MenuItem"
          icon="Bell"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.tenantPlusMenuItem') as unknown as MenuItem"
          icon="PlusMembership"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.signOutMenuItem') as unknown as MenuItem"
          icon="SignOut"
          :currentRoute="route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
      </template>
    </template>
    <input id="utm_source" type="hidden" />
    <input id="utm_medium" type="hidden" />
    <input id="utm_campaign" type="hidden" />
    <router-view />
  </Is24Layout>
</template>

<style lang="scss">
@use './assets/globals-is24' as *;
@use './assets/variables' as *;

main[class^='Is24Layout_isWhiteLabel_'] {
  padding-top: 0;

  @include grid-media-query(sm) {
    padding-top: $space-400;
  }
}
</style>
